import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, X, Crown, Zap, Sparkles, Building2 } from "lucide-react";

const PLANS = [
  {
    name: "Free",
    price: "0",
    key: "free",
    icon: Zap,
    accent: "#64748b",
    features: [
      "10 one-time signup credits",
      "UGC Videos",
      "2 Scene UGC Videos",
      "Product Videos",
      "Watermarked videos",
    ],
    notIncluded: ["Monthly credits", "Hero Shots", "Wallet top-up", "Priority queue"],
    cta: "Get Started Free",
    ctaLink: "/signup",
    highlight: false,
  },
  {
    name: "Basic",
    price: "499",
    key: "beginner",
    icon: Sparkles,
    accent: "#FF5A36",
    credits: 50,
    imageEquiv: 25,
    videoEquiv: 5,
    features: [
      "UGC Videos",
      "2 Scene UGC Videos",
      "Product Videos",
      "Wallet top-up enabled - \u20B910/Credit",
    ],
    notIncluded: ["Hero Shots", "Category Explainer Videos", "Priority queue"],
    cta: "Sign Up to Subscribe",
    ctaLink: "/signup",
    highlight: false,
  },
  {
    name: "Pro",
    price: "1,999",
    key: "pro",
    icon: Crown,
    accent: "#d97706",
    credits: 200,
    imageEquiv: 100,
    videoEquiv: 20,
    features: [
      "UGC Videos",
      "2 Scene UGC Videos",
      "Product Videos",
      "Hero Shots",
      "Category Explainer Videos",
      "Wallet top-up enabled - \u20B910/Credit",
      "Priority queue",
    ],
    notIncluded: [],
    cta: "Sign Up to Subscribe",
    ctaLink: "/signup",
    highlight: true,
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

export default function PricingPublic() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen relative overflow-hidden" style={{ backgroundColor: "#F5F5F0" }} data-testid="pricing-public-page">
      {/* ── HEADER ── */}
      <header
        className="fixed top-0 left-0 right-0 z-50 border-b"
        style={{ backgroundColor: "rgba(245,245,240,0.85)", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", borderColor: "rgba(0,0,0,0.06)" }}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="pricing-public-logo">
            <img
              src="https://customer-assets.emergentagent.com/job_2d4aff0d-e5b7-449b-860f-09baa683a8bc/artifacts/b3uq1zlx_Logo.png"
              alt="Prysh AI"
              className="w-9 h-9 object-contain"
            />
            <span className="font-display text-lg font-semibold" style={{ color: "#1A1A1A" }}>Prysh AI</span>
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm font-medium transition-colors"
            style={{ color: "rgba(26,26,26,0.5)" }}
            data-testid="pricing-public-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      {/* ── HERO ── */}
      <section className="pt-32 pb-12 px-6 md:px-12">
        <div className="max-w-3xl mx-auto text-center">
          <motion.h1
            {...fade(0.1)}
            className="font-display text-4xl sm:text-5xl font-bold tracking-tight leading-[1.05]"
            style={{ color: "#FF5A36" }}
            data-testid="pricing-public-headline"
          >
            Choose Your Plan
          </motion.h1>
          <motion.p {...fade(0.2)} className="mt-4 text-base lg:text-lg" style={{ color: "rgba(26,26,26,0.5)" }}>
            Start free and scale as you grow. Upgrade anytime.
          </motion.p>
          <motion.div {...fade(0.3)} className="inline-flex items-center gap-2 mt-6 px-4 py-1.5 rounded-full" style={{ backgroundColor: "#FF5A3612", border: "1px solid #FF5A3640" }} data-testid="public-launch-banner">
            <Sparkles className="w-4 h-4" style={{ color: "#FF5A36" }} />
            <span className="text-sm font-semibold" style={{ color: "#FF5A36" }}>Launch offer — 50% off for our first 50 customers</span>
          </motion.div>
        </div>
      </section>

      {/* ── PLAN CARDS ── */}
      <section className="px-6 md:px-12 pb-20">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PLANS.map((plan, i) => (
            <motion.div
              key={plan.key}
              {...fade(i * 0.1)}
              className={`relative bg-white rounded-2xl p-8 flex flex-col border transition-all ${
                plan.highlight ? "md:-mt-3 md:mb-3 shadow-lg" : ""
              }`}
              style={{
                borderColor: plan.highlight ? plan.accent : "rgba(0,0,0,0.06)",
                borderWidth: plan.highlight ? 2 : 1,
                boxShadow: plan.highlight ? `0 8px 40px rgba(217,119,6,0.12)` : "0 2px 12px rgba(0,0,0,0.03)",
              }}
              data-testid={`pricing-card-${plan.key}`}
            >
              {plan.highlight && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 text-white text-xs font-bold px-4 py-1 rounded-full"
                  style={{ backgroundColor: plan.accent }}
                >
                  POPULAR
                </div>
              )}

              <div className="flex items-center gap-2 mb-5">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${plan.accent}15` }}
                >
                  <plan.icon className="w-5 h-5" style={{ color: plan.accent }} />
                </div>
                <h3 className="text-lg font-display font-semibold" style={{ color: "#1A1A1A" }}>{plan.name}</h3>
              </div>

              <div className="mb-1">
                {plan.price !== "0" && (
                  <div className="flex items-center gap-2 mb-1" data-testid={`public-launch-price-${plan.key}`}>
                    <span className="text-base line-through" style={{ color: "rgba(26,26,26,0.35)" }}>
                      ₹{(parseInt(plan.price.replace(/,/g, "")) * 2).toLocaleString("en-IN")}
                    </span>
                    <span className="text-[11px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: "#FF5A3615", color: "#FF5A36" }}>
                      50% OFF · LAUNCH
                    </span>
                  </div>
                )}
                <span className="text-3xl font-bold" style={{ color: "#1A1A1A" }}>
                  {plan.price === "0" ? "Free" : `\u20B9${plan.price}`}
                </span>
                {plan.price !== "0" && <span className="text-sm ml-1" style={{ color: "rgba(26,26,26,0.4)" }}> + GST/month</span>}
              </div>

              {plan.credits && (
                <div className="rounded-xl border p-4 mb-5 space-y-3" style={{ backgroundColor: "rgba(26,26,26,0.03)", borderColor: "rgba(26,26,26,0.08)" }}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" style={{ color: plan.accent }} />
                    <span className="text-lg font-bold" style={{ color: "#1A1A1A" }}>{plan.credits} credits/mo.</span>
                  </div>
                  <div className="space-y-1 text-sm" style={{ color: "rgba(26,26,26,0.5)" }}>
                    <p>= {plan.imageEquiv || Math.floor(plan.credits / 2)} AI Image Generations</p>
                    <p>~ {plan.videoEquiv || Math.floor(plan.credits / 6)} AI Video Ads</p>
                  </div>
                </div>
              )}
              {!plan.credits && (
                <div className="rounded-xl border p-4 mb-5 space-y-3" style={{ backgroundColor: "rgba(26,26,26,0.03)", borderColor: "rgba(26,26,26,0.08)" }}>
                  <div className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4" style={{ color: "#64748b" }} />
                    <span className="text-lg font-bold" style={{ color: "#1A1A1A" }}>10 one-time credits</span>
                  </div>
                  <div className="space-y-1 text-sm" style={{ color: "rgba(26,26,26,0.5)" }}>
                    <p>= 5 AI Image Generations</p>
                    <p>~ 1 AI Video Ad</p>
                  </div>
                </div>
              )}

              <div className="flex-1 space-y-3 mb-8">
                {plan.features.map((f, j) => (
                  <div key={j} className="flex items-start gap-2.5 text-sm">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 shrink-0" />
                    <span style={{ color: "rgba(26,26,26,0.7)" }}>{f}</span>
                  </div>
                ))}
                {plan.notIncluded.map((f, j) => (
                  <div key={j} className="flex items-start gap-2.5 text-sm">
                    <X className="w-4 h-4 mt-0.5 shrink-0" style={{ color: "rgba(26,26,26,0.2)" }} />
                    <span style={{ color: "rgba(26,26,26,0.3)" }}>{f}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={() => navigate(plan.ctaLink)}
                className="w-full rounded-full h-11 text-sm font-medium border-0"
                style={{
                  backgroundColor: plan.highlight ? plan.accent : "#FF5A36",
                  color: "#FFFFFF",
                }}
                data-testid={`pricing-cta-${plan.key}`}
              >
                {plan.cta}
              </Button>
            </motion.div>
          ))}

          {/* Enterprise Card */}
          <motion.div
            {...fade(0.3)}
            className="relative rounded-2xl p-6 flex flex-col"
            style={{
              borderWidth: 1,
              borderStyle: "solid",
              borderColor: "rgba(26,26,26,0.1)",
              backgroundColor: "#FFFFFF",
              boxShadow: "0 2px 12px rgba(0,0,0,0.03)",
            }}
            data-testid="pricing-enterprise-card"
          >
            <div className="flex items-center gap-2 mb-5">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: "rgba(26,26,26,0.05)" }}
              >
                <Building2 className="w-5 h-5" style={{ color: "#1A1A1A" }} />
              </div>
              <h3 className="text-lg font-display font-semibold" style={{ color: "#1A1A1A" }}>Enterprise</h3>
            </div>

            <p className="text-sm mb-6" style={{ color: "rgba(26,26,26,0.45)" }}>
              For marketing and agency teams and organizations that need custom plans, services, API, and more
            </p>

            <p className="text-3xl font-bold mb-8" style={{ color: "#1A1A1A" }}>Let's talk</p>

            <div className="mt-auto">
              <Button
                onClick={() => navigate("/book-demo")}
                className="w-full rounded-full h-11 text-sm font-medium border-0"
                style={{ backgroundColor: "rgba(26,26,26,0.06)", color: "#1A1A1A" }}
                data-testid="pricing-enterprise-cta"
              >
                Book a Demo — Get 1 Free Ad
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Refund note */}
        <motion.p {...fade(0.3)} className="text-center text-xs mt-8" style={{ color: "rgba(26,26,26,0.35)" }}>
          All purchases are non-refundable once credits are used. Please review plans before purchasing.{" "}
          <Link to="/refund" className="underline hover:opacity-80">Refund Policy</Link>
        </motion.p>
      </section>

      {/* ── FOOTER ── */}
      <footer className="relative z-10 border-t py-12 px-6 md:px-12" style={{ borderColor: "rgba(0,0,0,0.06)" }}>
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-xs" style={{ color: "rgba(26,26,26,0.25)" }}>
            &copy; {new Date().getFullYear()} Prysh AI Labs. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <Link to="/about" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }}>About</Link>
            <Link to="/book-demo" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }}>Contact</Link>
            <Link to="/terms" className="text-xs font-medium" style={{ color: "rgba(26,26,26,0.4)" }}>Terms</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
